/**
 * 이용 개요의 KPI — 사용량 쪽의 첫 답 (DESIGN §8.9).
 *
 * **고장 KPI 를 그대로 쓰지 않는 이유.** `KpiStrip` 은 가동률을 3정의 동시에
 * 보여주는 것이 존재 이유다 (§15.3 "단일 고장률 숫자는 존재하지 않는다").
 * 사용량 화면에 그대로 얹으면 화면의 절반이 고장 이야기가 되어, 두 대분류를
 * 나눈 의미가 사라진다.
 *
 * **이용률은 정의 3분할을 하지 않는다.** 고장 정의(strict/standard/user_facing)는
 * *어떤 상태코드를 DOWN 으로 셀 것인가*의 문제다. 충전중(stat=3)은 세 정의
 * 어디서도 DOWN 이 아니므로 분자가 바뀌지 않는다. 분모(관측 시간)도 같다.
 * 그래서 모드를 바꿔도 이용률은 움직이지 않는다 — 3개를 나란히 두면 같은 값이
 * 세 번 나와 "정의에 따라 달라진다"는 거짓 인상을 준다.
 */

import { useQuery } from '@tanstack/react-query'

import { fetchEnvelope } from '@/lib/api'
import { int, pct } from '@/lib/fmt'
import type { Envelope, SummaryData } from '@/lib/types'
import { filterKey, toQueryFilters, useFilters } from '@/stores/filters'
import './UsageKpi.css'

export function UsageKpi() {
  const filters = useFilters()
  const q = useQuery({
    queryKey: ['metrics/summary', 'usage', filterKey(filters)],
    queryFn: ({ signal }) =>
      fetchEnvelope<SummaryData>('/metrics/summary', toQueryFilters(filters), {}, signal),
    staleTime: 5 * 60 * 1000,
  })

  const env = q.data as Envelope<SummaryData> | undefined
  const d = env?.data
  const filtered =
    d?.n_chargers != null &&
    env?.meta.population_n != null &&
    d.n_chargers !== env.meta.population_n

  if (q.error) {
    return (
      <section className="ukpi ukpi--error" role="alert">
        데이터를 불러오지 못했습니다. 서버 상태를 확인하거나 다시 시도하세요.
      </section>
    )
  }

  return (
    <section className="ukpi" aria-label="이용 핵심 지표" aria-busy={q.isLoading}>
      <div className="ukpi__lead">
        <h2 className="ukpi__label">
          관측 이용률
          <span className="ukpi__sub">관측된 시간 중 충전중(stat=3) 비중</span>
        </h2>
        <p className="ukpi__big num">{pct(d?.utilization, 1)}</p>
        <p className="ukpi__note">
          고장 정의를 바꿔도 이 값은 움직이지 않습니다 — 충전중은 어떤 정의에서도 DOWN 이
          아닙니다.
        </p>
      </div>

      <dl className="ukpi__facts">
        <div className="ukpi__fact">
          <dt>관측 커버리지</dt>
          <dd>
            <span className="ukpi__factval num">{pct(env?.meta.coverage, 1)}</span>
            <span className="ukpi__factnote">
              나머지는 <strong>관측되지 않은 시간</strong>입니다 — 0 이 아니라 모름
            </span>
          </dd>
        </div>
        <div className="ukpi__fact">
          <dt>충전에 쓰인 시간</dt>
          <dd>
            <span className="ukpi__factval num">
              {d == null || d.t_obs_min == null
                ? '—'
                : `${int(Math.round((d.utilization ?? 0) * d.t_obs_min / 1440))}일`}
            </span>
            <span className="ukpi__factnote">관측 시간 {int(Math.round((d?.t_obs_min ?? 0) / 1440))}일 중</span>
          </dd>
        </div>
        <div className="ukpi__fact">
          {/* `meta.population_n` 은 전역이라 필터를 타지 않는다 — 필터를 건
              화면에서 그 수를 「모집단」이라 쓰면 거짓말이 된다 (§8.6.10) */}
          <dt>{filtered ? '이 화면의 충전기' : '분석 모집단'}</dt>
          <dd>
            <span className="ukpi__factval num">
              {d?.n_chargers == null ? '—' : int(d.n_chargers)}
            </span>
            <span className="ukpi__factnote">
              {filtered
                ? `필터를 적용한 수 — 전체 모집단 ${int(env?.meta.population_n)}기 중`
                : '철거·좌표오류·미관측을 뺀 수'}
            </span>
          </dd>
        </div>
      </dl>
    </section>
  )
}
